import { useTranslation } from 'next-i18next'
import { useEffect, useMemo, useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Equals from '~/bootcamp/Activities/components/Equals'
import Input from '~/bootcamp/Activities/components/Input'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import PEMDASActivityWrapper from '~/bootcamp/Activities/PEMDAS/components/PEMDASActivityWrapper'

import useFocus from '../../hooks/useFocus'

import { generateFromNumberAndOperators, generateNumbersAndResult } from './helpers'
import { Ranges, TaskHistory } from './types'

const MIN_NUMBER_1 = 1
const MAX_NUMBER_1 = 10

const MIN_NUMBER_2 = 2
const MAX_NUMBER_2 = 25

function MultiDivision({
  answerState,
  difficultyLevel,
  onNextQuestionClick,
  onAnswerStateChange,
  onInstanceChange,
  actionButtonsFC,
  autofocus = true,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation(['bootcamp', 'generics'])
  const [answer, setAnswer] = useState('')
  const [inputKey, setInputKey] = useState<string>(uuid())
  const { minNumber, maxNumber } = useMemo(() => getRanges(difficultyLevel), [difficultyLevel])
  const [taskHistory, setTaskHistory] = useState<TaskHistory[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)
  const [{ expression, isFirstDivision, expressionValues, result }, setExpressionData] = useState({
    expression: '',
    isFirstDivision: true,
    expressionValues: [] as number[],
    result: 0,
  })

  useEffect(() => {
    const taskHistoryDividends = taskHistory.map((elem) => elem.dividend)
    const taskHistoryMultipliers = taskHistory.map((elem) => elem.multiplier)
    const _isFirstDivision = false // Math.random() < 0.5
    const operators: string[] = [
      _isFirstDivision ? t('bootcamp:signs.division') : '×',
      _isFirstDivision ? '×' : t('bootcamp:signs.division'),
    ]
    const numbersAndResult = generateNumbersAndResult(
      minNumber,
      maxNumber,
      _isFirstDivision,
      taskHistoryDividends,
      taskHistoryMultipliers,
    )
    const exp = generateFromNumberAndOperators(numbersAndResult.numbers, operators)
    setAnswer('')
    setExpressionData({
      expression: exp,
      isFirstDivision: _isFirstDivision,
      expressionValues: numbersAndResult.numbers,
      result: numbersAndResult.result,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxNumber, minNumber, taskHistory])

  useEffect(() => {
    onAnswerStateChange(null)
  }, [answer, onAnswerStateChange])

  useEmitInstance(
    onInstanceChange,
    expression
      ? `${expression.replaceAll('×', '\\cdot').replaceAll(t('bootcamp:signs.division'), '\\div')} = \\square`
      : null,
  )

  const handleOnCheckAnswerClick = (): void => {
    const isCorrect = Number(answer) === result
    onAnswerStateChange(isCorrect)
  }

  const handleOnNextQuestionClick = (): void => {
    setTaskHistory((prevState) => {
      return [
        { dividend: expressionValues[isFirstDivision ? 0 : 1], multiplier: expressionValues[isFirstDivision ? 2 : 0] },
        ...prevState,
      ].slice(0, difficultyLevel === 1 ? 3 : 5)
    })
    onAnswerStateChange(null)
    onNextQuestionClick()
    setInputKey(uuid())
  }
  const onEnterPress = (): void => {
    if (answer !== '') {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  return (
    <ActivityWrapper>
      <PEMDASActivityWrapper>
        <span className="tracking-widest">{expression}</span>
        <span className="block sm:inline-block">
          <Equals />
          <Input
            aria-label={t('generics:ariaLabelButtons.insertResult')}
            autoFocus={autofocus}
            isError={answerState === false}
            key={inputKey}
            maxDigits={3}
            onChange={(value) => setAnswer(value)}
            onEnter={onEnterPress}
            ref={inputRef}
            value={answer}
          />
        </span>
      </PEMDASActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid: answer !== '',
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: () => setAnswer(''),
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

function getRanges(difficultyLevel: number | undefined): Ranges {
  if (typeof difficultyLevel === 'undefined' || difficultyLevel === 1) {
    return {
      minNumber: MIN_NUMBER_1,
      maxNumber: MAX_NUMBER_1,
    }
  } else {
    return {
      minNumber: MIN_NUMBER_2,
      maxNumber: MAX_NUMBER_2,
    }
  }
}

export default MultiDivision
